"use client";

import { useEffect } from "react";
import { NATIVE_SHELL_COOKIE } from "@/lib/native-shell";
import { nativePlatformHeader } from "@/lib/utils";

/**
 * Records "this browser is actually the phone app" in a cookie so the SERVER can act
 * on it, not just the client.
 *
 * Why a cookie rather than a header: the pages that need to differ between app and
 * website are decided during a top-level document navigation, and a document
 * navigation carries no custom headers. Capacitor only tells the CLIENT what platform
 * it is on, and by then the wrong page has already been rendered and sent. The cookie
 * carries that answer forward to the next navigation.
 *
 * Consequence worth stating plainly: on the very first launch after install the server
 * has no cookie yet and, on an iOS binary older than 2026-08-23, no User-Agent marker
 * either, so the app briefly renders the website's landing page before the client
 * swap takes over. Every launch after that is decided server side. See
 * lib/native-shell.ts for the full detection story.
 *
 * Not a security control: nothing behind auth reads it, and the only thing a forged
 * value changes is which front door a browser is shown.
 */
export function NativeShellCookie() {
  useEffect(() => {
    const platform = nativePlatformHeader();
    if (!platform) return;

    const value = platform === "ios-capacitor" ? "ios" : "android";
    if (document.cookie.includes(`${NATIVE_SHELL_COOKIE}=${value}`)) return;

    // A year, refreshed on every launch, so it long outlives normal usage gaps.
    // Lax rather than Strict: the app follows links back into itself (deep links,
    // magic links, Stripe returns) and Strict would drop the cookie on those.
    document.cookie = `${NATIVE_SHELL_COOKIE}=${value}; path=/; max-age=31536000; samesite=lax; secure`;
  }, []);

  return null;
}
